import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRedo, FaTrashAlt, FaUpload } from 'react-icons/fa';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { NS, U3208ROLES } from '../../U3208';
import { useU3208Context } from '../../U3208Context';
import { useU3208PollU2200JobHandlers } from '../../hooks/useU3208PollU2200JobHandlers';
import { useU3208SourcesHandlers } from '../../hooks/useU3208SourcesHandlers';
import {
  u3208DeriveSourceStatus,
  u3208FormatIndexedAtOrg,
  u3208SourceDisplayName,
  type U3208DocSource,
  type U3208U2200Job,
} from '../../U3208Service';
import U3208UploadSourceDialog from '../common/U3208UploadSourceDialog';
import U3208DeleteSourceDlg from '../common/U3208DeleteSourceDlg';

interface U3208SourcesPanelProps {
  corpusId: string | undefined;
  readOnly: boolean;
}

const U3208SourcesPanel: React.FC<U3208SourcesPanelProps> = ({ corpusId, readOnly }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { initLU } = useU3208Context();
  const { pollUntilDone } = useU3208PollU2200JobHandlers();
  const {
    sources,
    setSources,
    loading,
    submitting,
    loadSources,
    uploadSource,
    deleteSource,
    reindexSource,
    reindexCorpus,
  } = useU3208SourcesHandlers(corpusId);

  const [job, setJob] = useState<U3208U2200Job | null>(null);
  const [jobRunning, setJobRunning] = useState(false);
  const [failedSourceIds, setFailedSourceIds] = useState<Set<string>>(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState<U3208DocSource | null>(null);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  const busy = loading || submitting || jobRunning;
  const canAdd = !readOnly && hasRole(U3208ROLES.ADD);
  const canEdit = !readOnly && hasRole(U3208ROLES.EDIT);
  const canDelete = !readOnly && hasRole(U3208ROLES.DELETE);

  const runJob = async (created: U3208U2200Job | null, sourceIdForFail?: string) => {
    if (!created) return;
    setJob(created);
    setJobRunning(true);
    const terminal = await pollUntilDone(created.id, (update) => setJob(update));
    setJobRunning(false);
    if (terminal) setJob(terminal);
    if (terminal?.status === 'completed') {
      toast.success(t('toast.reindexOk', { ns: NS }));
      if (sourceIdForFail) {
        setFailedSourceIds((prev) => {
          const next = new Set(prev);
          next.delete(sourceIdForFail);
          return next;
        });
      }
    } else if (terminal?.status === 'failed') {
      toast.error(t('toast.jobFailed', { ns: NS }), t('errorTitle', { ns: NS }));
      if (sourceIdForFail) {
        setFailedSourceIds((prev) => new Set(prev).add(sourceIdForFail));
      }
    }
    await loadSources();
  };

  const handleUploaded = async (result: { source: U3208DocSource; job: U3208U2200Job }) => {
    setSources((prev) => {
      if (prev.some((s) => s.id === result.source.id)) return prev;
      return [result.source, ...prev];
    });
    await runJob(result.job, result.source.id);
  };

  const handleReindexSource = async (sourceId: string) => {
    const created = await reindexSource(sourceId);
    await runJob(created, sourceId);
  };

  const handleReindexCorpus = async () => {
    const created = await reindexCorpus();
    await runJob(created);
  };

  const handleConfirmDelete = async () => {
    if (!sourceToDelete) return;
    const ok = await deleteSource(sourceToDelete.id);
    if (ok) setSourceToDelete(null);
  };

  const jobBanner =
    jobRunning || job?.status === 'pending' || job?.status === 'running'
      ? t('status.pending', { ns: NS })
      : job?.status === 'failed'
        ? t('toast.jobFailed', { ns: NS })
        : job?.status === 'completed'
          ? t('toast.reindexOk', { ns: NS })
          : null;

  if (!corpusId || corpusId === 'new') {
    return (
      <div className="mt-6 rounded-sm border border-dashed border-gray-300 p-4 text-sm text-gray-500">
        {t('msgSaveBeforeSources', { ns: NS })}
      </div>
    );
  }

  return (
    <div className="mt-6 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">{t('detail.sources', { ns: NS })}</h2>
        <div className="flex flex-wrap gap-2">
          {canAdd ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-sm bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:opacity-50"
              onClick={() => setUploadOpen(true)}
              disabled={busy}
            >
              <FaUpload className="h-3.5 w-3.5" />
              {t('action.upload', { ns: NS })}
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
              onClick={() => void handleReindexCorpus()}
              disabled={busy || sources.length === 0}
            >
              <FaRedo className="h-3.5 w-3.5" />
              {t('action.reindexCorpus', { ns: NS })}
            </button>
          ) : null}
        </div>
      </div>

      {jobBanner ? (
        <div
          className={`rounded border px-3 py-2 text-sm ${
            job?.status === 'failed'
              ? 'border-red-300 bg-red-50 text-red-800'
              : job?.status === 'completed'
                ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                : 'border-gray-200 bg-gray-50 text-gray-800'
          }`}
          role="status"
        >
          <p className="font-medium">{jobBanner}</p>
          {job?.errorMessage ? <p className="mt-1 text-xs">{job.errorMessage}</p> : null}
        </div>
      ) : null}

      <div className="min-w-0 overflow-x-auto rounded-sm border border-gray-200">
        <table className="w-full min-w-160 border-collapse text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 font-medium">{t('col.displayName', { ns: NS })}</th>
              <th className="px-3 py-2 font-medium">{t('col.status', { ns: NS })}</th>
              <th className="px-3 py-2 font-medium">{t('col.chunks', { ns: NS })}</th>
              <th className="px-3 py-2 font-medium">{t('col.indexedAt', { ns: NS })}</th>
              <th className="px-3 py-2 font-medium">{t('col.actions', { ns: NS })}</th>
            </tr>
          </thead>
          <tbody>
            {loading && sources.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  {t('msgLoadingSources', { ns: NS })}
                </td>
              </tr>
            ) : sources.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  {t('msgEmptySources', { ns: NS })}
                </td>
              </tr>
            ) : (
              sources.map((row) => {
                const status = u3208DeriveSourceStatus(row, failedSourceIds.has(row.id));
                const statusLabel =
                  status === 'indexed'
                    ? t('status.indexed', { ns: NS })
                    : status === 'failed'
                      ? t('status.failed', { ns: NS })
                      : t('status.pending', { ns: NS });
                return (
                  <tr key={row.id} className="border-t border-gray-200">
                    <td className="max-w-[24rem] truncate px-3 py-2 font-medium" title={u3208SourceDisplayName(row)}>
                      {u3208SourceDisplayName(row)}
                    </td>
                    <td className="px-3 py-2">{statusLabel}</td>
                    <td className="px-3 py-2 tabular-nums">{row.chunkCount}</td>
                    <td className="px-3 py-2 tabular-nums">{u3208FormatIndexedAtOrg(row.indexedAt)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        {canEdit ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline disabled:opacity-50"
                            onClick={() => void handleReindexSource(row.id)}
                            disabled={busy}
                          >
                            <FaRedo className="h-3 w-3" />
                            {t('action.reindex', { ns: NS })}
                          </button>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline disabled:opacity-50"
                            onClick={() => setSourceToDelete(row)}
                            disabled={busy}
                          >
                            <FaTrashAlt className="h-3 w-3" />
                            {t('action.deleteSource', { ns: NS })}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <U3208UploadSourceDialog
        isOpen={uploadOpen}
        allowedMimeTypes={initLU.allowedMimeTypes}
        maxFileSizeBytes={initLU.maxFileSizeBytes}
        submitting={submitting}
        onClose={() => setUploadOpen(false)}
        onUpload={uploadSource}
        onUploaded={(result) => void handleUploaded(result)}
      />

      <U3208DeleteSourceDlg
        isOpen={!!sourceToDelete}
        source={sourceToDelete}
        submitting={submitting}
        onClose={() => setSourceToDelete(null)}
        onConfirm={() => void handleConfirmDelete()}
      />
    </div>
  );
};

export default U3208SourcesPanel;
